// Phase 10e — portal-side billing read shapes (facility + homecare).
//
// Designed to leak nothing operator-internal: no Payload field on
// PaymentEvent (could carry operator notes / Stripe-internal data),
// no facility-specific audit fields, no operator userIds.
import { z } from 'zod';
import { InvoiceLineResponseSchema } from './invoice-line.schema.js';
import { InvoiceResponseSchema } from './invoice.schema.js';
import { DunningEventResponseSchema } from './dunning-admin.schema.js';
import { PaymentEventTypeSchema } from './enums.schema.js';
export const PortalPaymentEventSchema = z.object({
    id: z.string().uuid(),
    eventType: PaymentEventTypeSchema,
    amountCents: z.number().int(),
    externalRef: z.string().nullable(),
    recordedAt: z.string().datetime(),
});
export const PortalInvoiceListItemSchema = InvoiceResponseSchema.extend({
    lineCount: z.number().int().min(0),
});
export const PortalInvoiceListResponseSchema = z.object({
    items: z.array(PortalInvoiceListItemSchema),
    totalCount: z.number().int().min(0),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
export const PortalInvoiceDetailResponseSchema = InvoiceResponseSchema.extend({
    lines: z.array(InvoiceLineResponseSchema),
    paymentEvents: z.array(PortalPaymentEventSchema),
    dunningEvents: z.array(DunningEventResponseSchema),
    /** Short-TTL signed download URL for the rendered invoice PDF (2026-09-21).
     *  Facility invoices only; null until the render-invoice-pdf job has run. */
    pdfUrl: z.string().nullable(),
});
export const PortalInvoiceListQuerySchema = z.object({
    status: z
        .enum(['DRAFT', 'ISSUED', 'PARTIAL', 'PAID', 'OVERDUE', 'DISPUTED', 'VOIDED'])
        .optional(),
    /**
     * Coarse lifecycle grouping — the split the buyer portal actually renders
     * ("Outstanding" vs "Paid & closed").
     *
     * 2026-07-23 pre-deploy audit (U1): the portal previously fetched ONE
     * unfiltered page and split it client-side. Because the server paged at 20
     * and ordered by `status: 'asc'` — and Postgres native-enum ASC follows
     * declaration order, in which PAID (4) precedes OVERDUE (5) — a facility
     * with 20+ paid invoices had its overdue invoice pushed onto an unreachable
     * page 2 while the Outstanding panel rendered "Nothing outstanding — you're
     * all paid up." Reproduced live against a seeded facility. Filtering
     * server-side makes that unrepresentable at any data volume.
     */
    state: z.enum(['open', 'closed', 'all']).default('all'),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(20),
});
/** Statuses that need no further buyer action. Single source of truth — the
 *  portal no longer keeps its own copy; it asks the server via `state`. */
export const PORTAL_CLOSED_INVOICE_STATUSES = ['PAID', 'VOIDED'];
//# sourceMappingURL=portal-billing.schema.js.map